import React from "react";
import { connect } from "react-redux";
import { Badge, Button, Card, Col, Spinner, Table } from "react-bootstrap";
import PropTypes from "prop-types";

import CsLineIcons from "../../cs-line-icons";
import {
    all as getAllNotifications,
    read as readNotification,
} from "../../actions/notification";
import "../../layout/notifications/style.css";

const formatDate = (value) => {
    if (!value) return "Not available";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Not available";
    return date.toLocaleString();
};

const styleCaption = (style) => {
    switch (style) {
        case "UR":
            return "Urgent";
        case "LO":
            return "Low";
        case "3D":
            return "Activity";
        default:
            return "Notification";
    }
};

class NotificationsPage extends React.Component {
    state = {
        error: "",
        items: [],
        loading: true,
        readingUuid: null,
    };

    componentDidMount() {
        this.loadData();
    }

    loadData = async () => {
        this.setState({ error: "", loading: true });
        const items = await this.props.getAllNotifications();
        this.setState({
            error: items === null ? "Notifications could not be loaded. Try again." : "",
            items: Array.isArray(items) ? items : [],
            loading: false,
        });
    };

    onRead = async (uuid) => {
        this.setState({ readingUuid: uuid });
        const result = await this.props.readNotification(uuid);
        if (result?.success) {
            await this.loadData();
        } else {
            this.setState({
                error: "This notification could not be marked as read. Try again.",
            });
        }
        this.setState({ readingUuid: null });
    };

    renderBody = () => {
        const { error, items, loading, readingUuid } = this.state;

        if (loading) {
            return (
                <div className="notification-page-empty" role="status">
                    <Spinner animation="border" />
                    <span className="visually-hidden">Loading notifications</span>
                </div>
            );
        }

        if (error && !items.length) {
            return (
                <div className="notification-page-empty flex-column gap-3" role="alert">
                    <span>{error}</span>
                    <Button size="sm" variant="outline-primary" onClick={this.loadData}>
                        Try again
                    </Button>
                </div>
            );
        }

        if (!items.length) {
            return <div className="notification-page-empty">No notifications yet.</div>;
        }

        return (
            <>
                {error ? <div className="alert alert-danger mx-3 mt-3 mb-0" role="alert">{error}</div> : null}
                <Table responsive hover className="mb-0 notification-page-table">
                    <thead>
                        <tr>
                            <th>Notification</th>
                            <th style={{ width: "140px" }}>Status</th>
                            <th style={{ width: "190px" }}>Active</th>
                            <th style={{ width: "190px" }}>Read</th>
                            <th className="text-end" style={{ width: "130px" }} />
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item) => {
                            const unread = !item.readDate;
                            const reading = readingUuid === item.uuid;
                            return (
                                <tr key={item.uuid}>
                                    <td>
                                        <div className="notification-page-title">
                                            <CsLineIcons icon={unread ? "bell" : "form-check"} size="15" />
                                            <span>{item.title || "Notification"}</span>
                                        </div>
                                        <div className="text-muted">{item.description}</div>
                                    </td>
                                    <td>
                                        <Badge bg={unread ? "primary" : "secondary"}>
                                            {unread ? "Unread" : "Read"}
                                        </Badge>
                                        <div className="small text-muted mt-1">{styleCaption(item.style)}</div>
                                    </td>
                                    <td>{formatDate(item.activeDate)}</td>
                                    <td>{formatDate(item.readDate)}</td>
                                    <td className="text-end">
                                        {unread ? (
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                disabled={reading}
                                                onClick={() => this.onRead(item.uuid)}
                                            >
                                                {reading ? "Marking..." : "Mark read"}
                                            </Button>
                                        ) : null}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </>
        );
    };

    render() {
        return (
            <Col xs="12">
                <Card body className="mb-5 notification-page-card">
                    {this.renderBody()}
                </Card>
            </Col>
        );
    }
}

NotificationsPage.propTypes = {
    getAllNotifications: PropTypes.func.isRequired,
    readNotification: PropTypes.func.isRequired,
};

export { formatDate, styleCaption, NotificationsPage };
export default connect(null, {
    getAllNotifications,
    readNotification,
})(NotificationsPage);
